// ═══════════════════════════════════════════════════════════════
//   ABS DRESS DESIGNER — App.js
//   Root application — Global Header · Auth Modal · Page Router
//
//   Feature 1: Login accepts Email OR Mobile Number + password
//              (toggle buttons inside Sign In modal)
//   Feature 2: Register checks both email AND phone for duplicates
//              (backend returns specific error message for each)
//
//   BUG FIXES in this version:
//   - am-tab active class had double-space ('  am-on') — fixed to ' am-on'
//   - @import removed from runtime style tag (fonts via index.html)
//   - :root defined once only — no duplication with page components
//   - All form inputs are plain JSX (no sub-components) — no focus loss
// ═══════════════════════════════════════════════════════════════

import React, {
  useState, useEffect, useCallback,
  createContext, useContext,
} from 'react';
import axios from 'axios';
// Lazy-loaded pages — each page chunk is only downloaded when first visited
const ABSDressDesigner = React.lazy(() => import('./components/ABSDressDesigner'));
const OrderForm        = React.lazy(() => import('./components/OrderForm'));
const AdminDashboard   = React.lazy(() => import('./components/AdminDashboard'));
const OrderTracking    = React.lazy(() => import('./components/OrderTracking'));
const PortfolioGallery = React.lazy(() => import('./components/PortfolioGallery'));
const TermsPolicy      = React.lazy(() => import('./components/TermsPolicy'));
const StyleGuide       = React.lazy(() => import('./components/StyleGuide'));
const CustomerDashboard= React.lazy(() => import('./components/CustomerDashboard'));
const FAQPage          = React.lazy(() => import('./components/FAQPage'));
const MeasurementGuide = React.lazy(() => import('./components/MeasurementGuide'));
const AboutUs          = React.lazy(() => import('./components/AboutUs'));
const BecomePartner          = React.lazy(() => import('./components/BecomePartner'));
const AIMeasurementAdvisor   = React.lazy(() => import('./components/AIMeasurementAdvisor'));
const AIStylingAdvisor       = React.lazy(() => import('./components/AIStylingAdvisor'));
const MultiLanguageSupport   = React.lazy(() => import('./components/MultiLanguageSupport'));

export const API_URL   = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
export const AppContext = createContext(null);
export const useApp    = () => useContext(AppContext);

// ── URL ↔ page routing ─────────────────────────────────────────
// The app uses a state-based router (setPage). These maps keep the address
// bar in sync so every page has its own deep-linkable URL and browser
// back/forward works. nginx serves index.html for any path (SPA fallback),
// so refreshing on a deep link loads the app and we resolve the page here.
export const PAGE_PATHS = {
  home:          '/',
  order:         '/order',
  track:         '/track',
  gallery:       '/gallery',
  style:         '/style-finder',
  measurements:  '/measurement-guide',
  faq:           '/faq',
  'ai-measure':  '/ai-measurement-advisor',
  'ai-style':    '/ai-styling-advisor',
  multilang:     '/languages',
  about:         '/about',
  partner:       '/become-partner',
  terms:         '/terms',
  dashboard:     '/dashboard',
  admin:         '/admin',
};
const PATH_TO_PAGE = Object.entries(PAGE_PATHS).reduce((acc, [pg, path]) => {
  acc[path] = pg;
  return acc;
}, {});
export const pageToPath = (pg) => PAGE_PATHS[pg] || '/';
export const pathToPage = (pathname) => {
  const clean = (pathname || '/').replace(/\/+$/, '') || '/';
  return PATH_TO_PAGE[clean] || 'home';
};

// ── Axios interceptors ─────────────────────────────────────────
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('abs_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401 && !err.config?.url?.includes('/auth/')) {
      localStorage.removeItem('abs_token');
      delete axios.defaults.headers.common['Authorization'];
    }
    return Promise.reject(err);
  }
);

// ══════════════════════════════════════════════════════════════
//   GLOBAL STYLES
//   NOTE: No @import here — fonts loaded via <link> in index.html
//   NOTE: :root defined ONCE here — all page components use these vars
// ══════════════════════════════════════════════════════════════
const GLOBAL_STYLES = `
  :root {
    /* Default/fallback before GlobalHeader's ResizeObserver measures the
       real height — overridden dynamically once the header mounts, so
       page content always clears it correctly even when the nav wraps
       to two lines on narrow screens or busy accounts. */
    --gh-header-h: 70px;
    --gold:      #C9A84C;
    --gold-lt:   #E8C96D;
    --gold-pale: #F5E6BB;
    --ivory:     #FAF6EE;
    --ivory-d:   #F0E8D8;
    --cream:     #EDE0C8;
    --dark:      #1A1410;
    --dark-s:    #2C2318;
    --dark-t:    #3A2A1E;
    --brown:     #3D2C1E;
    --brown-l:   #5C4433;
    --white:     #FFFFFF;
    --sage:      #7A9B76;
    --rose:      #C4736A;
    /* ── Light "accent section" palette ──────────────────────────
       Replaces the old near-black section backgrounds (header, hero,
       footer, banners) sitewide, per design change: dark backgrounds
       read as harsh/hard-to-read for many visitors. These sections
       now use a warm cream/gold gradient instead of dark brown/black,
       with dark text restored for contrast (the old white/light-gray
       text that worked on dark backgrounds is illegible here, so every
       former dark-section text color is updated alongside this).
       --accent-bg-1/2/3 mirror the old --dark/--dark-s/--dark-t roles
       (gradient stops), just inverted to light. */
    --accent-bg-1: #FBF4E4;
    --accent-bg-2: #F5E9CC;
    --accent-bg-3: #EFDFB4;
    --accent-border: rgba(201,168,76,0.35);
    --accent-text:    #1A1410;  /* main heading/body text on accent sections */
    --accent-text-soft: #5C4433; /* secondary/muted text on accent sections */
    --font-d:    'Cormorant Garamond', Georgia, serif;
    --font-b:    'Jost', Arial, sans-serif;
    /* Readable text scale */
    --text-xs:   0.8rem;
    --text-sm:   0.9rem;
    --text-md:   1rem;
    --text-lg:   1.1rem;
  }
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; font-size: 16px; }
  body {
    font-family: var(--font-b);
    background: var(--ivory);
    color: var(--dark);
    overflow-x: hidden;
    -webkit-font-smoothing: antialiased;
  }
  ::-webkit-scrollbar { width: 5px; }
  ::-webkit-scrollbar-track { background: var(--ivory-d); }
  ::-webkit-scrollbar-thumb { background: var(--gold); border-radius: 3px; }

  /* ══ GLOBAL HEADER ══ */
  .gh-header {
    position: fixed; top: 0; left: 0; right: 0; z-index: 900;
    min-height: 70px;
    background: rgba(251,244,228,0.97);
    backdrop-filter: blur(14px);
    border-bottom: 1px solid var(--accent-border);
    display: flex; align-items: center; justify-content: space-between;
    padding: 10px 48px;
    flex-wrap: wrap;
    row-gap: 8px;
  }
  .gh-logo {
    display: flex; align-items: center; gap: 13px;
    cursor: pointer; user-select: none;
  }
  .gh-logo-badge {
    width: 42px; height: 42px; border-radius: 50%;
    background: linear-gradient(135deg, var(--gold), var(--gold-lt));
    display: flex; align-items: center; justify-content: center;
    font-family: var(--font-d); font-size: 1rem; font-weight: 600;
    color: var(--dark); flex-shrink: 0;
  }
  .gh-logo-name {
    font-family: var(--font-d); font-size: 1.25rem; font-weight: 600;
    color: var(--accent-text); letter-spacing: 2px; line-height: 1; display: block;
  }
  .gh-logo-sub {
    display: none;
  }
  .gh-nav { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
  .gh-menu { position: relative; }
  .gh-menu-btn {
    font-family: var(--font-b); font-size: 0.82rem; font-weight: 400;
    letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--accent-text-soft); background: none; border: none; cursor: pointer;
    padding: 6px 12px; border-radius: 2px; transition: color 0.2s;
    display: flex; align-items: center; gap: 4px;
  }
  .gh-menu-btn:hover, .gh-menu-btn.gh-active { color: #7A6000; }
  .gh-menu-btn::after { content: '▾'; font-size: .65rem; opacity: .7; }
  .gh-dropdown {
    display: none; position: absolute; top: 100%; left: 0; z-index: 200;
    background: var(--white); border: 1px solid var(--accent-border); border-radius: 3px;
    min-width: 180px; box-shadow: 0 8px 28px rgba(61,44,30,.18);
    overflow: hidden; padding-top: 6px; /* bridge gap so mouse doesn't lose hover */
  }
  .gh-dropdown-inner { background: var(--white); border: 1px solid var(--accent-border); border-radius: 3px; overflow: hidden; }
  .gh-menu.open .gh-dropdown { display: block; }
  .gh-dd-item {
    display: block; width: 100%; text-align: left; background: none; border: none;
    border-bottom: 1px solid var(--cream); cursor: pointer;
    font-family: var(--font-b); font-size: 0.85rem; letter-spacing: 1px;
    color: var(--accent-text-soft); padding: 11px 16px; transition: all .18s;
  }
  .gh-dd-item:hover { background: var(--ivory); color: #7A6000; }
  .gh-dd-item:last-child { border-bottom: none; }
  .gh-dd-item.active { color: #7A6000; background: var(--ivory); }
  .gh-btn {
    background: none; border: none; cursor: pointer;
    font-family: var(--font-b); font-size: 0.96rem; font-weight: 400;
    letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--accent-text-soft);
    padding: 8px 14px; border-radius: 2px;
    transition: color 0.2s; white-space: nowrap;
  }
  .gh-btn:hover, .gh-btn.gh-active { color: #7A6000; }
  .gh-btn-gold {
    background: linear-gradient(135deg, var(--gold), var(--gold-lt)) !important;
    color: var(--dark) !important; font-weight: 600 !important;
    padding: 9px 22px !important; border-radius: 2px;
    box-shadow: 0 2px 14px rgba(201,168,76,0.35); transition: all 0.25s;
  }
  .gh-btn-gold:hover { transform: translateY(-1px); box-shadow: 0 5px 22px rgba(201,168,76,0.5); }
  .gh-sep { width: 1px; height: 20px; background: var(--accent-border); margin: 0 4px; flex-shrink: 0; }
  .gh-cart-pill {
    display: flex; align-items: center; gap: 6px;
    background: linear-gradient(135deg, rgba(122,155,118,0.18), rgba(122,155,118,0.1));
    border: 1px solid rgba(122,155,118,0.5) !important;
    border-radius: 50px !important; padding: 6px 12px !important;
    color: #3A6035 !important; font-weight: 600 !important;
    letter-spacing: 0.3px !important; text-transform: none !important;
    font-size: 0.8rem !important; animation: gh-cart-pulse 2.2s ease-in-out infinite;
    white-space: nowrap;
  }
  .gh-cart-pill:hover { background: linear-gradient(135deg, rgba(122,155,118,0.28), rgba(122,155,118,0.16)); color: #2A5026 !important; }
  .gh-cart-count {
    background: #7A9B76; color: #fff; font-size: 0.74rem; font-weight: 700;
    border-radius: 50%; min-width: 18px; height: 18px; display: inline-flex;
    align-items: center; justify-content: center; padding: 0 2px;
  }
  @keyframes gh-cart-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(122,155,118,0.3); }
    50% { box-shadow: 0 0 0 5px rgba(122,155,118,0); }
  }
  .gh-user-pill {
    display: flex; align-items: center; gap: 8px;
    background: rgba(201,168,76,0.12); border: 1px solid var(--accent-border);
    border-radius: 50px; padding: 5px 14px 5px 7px;
    font-family: var(--font-b); font-size: 0.88rem; color: #7A6000; white-space: nowrap;
  }
  .gh-user-av {
    width: 24px; height: 24px; border-radius: 50%;
    background: linear-gradient(135deg, var(--gold), var(--gold-lt));
    display: flex; align-items: center; justify-content: center;
    font-size: 0.8rem; font-weight: 700; color: var(--dark); flex-shrink: 0;
  }
  .gh-page { padding-top: var(--gh-header-h); min-height: 100vh; }

  /* ══ AUTH MODAL ══ */
  .am-overlay {
    position: fixed; inset: 0; z-index: 1800;
    background: rgba(26,20,16,0.88); backdrop-filter: blur(8px);
    display: flex; align-items: center; justify-content: center;
    padding: 24px; overflow-y: auto;
    animation: am-fadein 0.22s ease;
  }
  @keyframes am-fadein { from { opacity: 0; } to { opacity: 1; } }
  .am-modal {
    background: #fff; border-radius: 6px;
    max-width: 460px; width: 100%; position: relative;
    box-shadow: 0 14px 60px rgba(26,20,16,0.28);
    animation: am-popin 0.32s cubic-bezier(0.34,1.4,0.64,1);
    overflow: hidden; margin: auto;
  }
  @keyframes am-popin {
    from { transform: scale(0.86) translateY(16px); opacity: 0; }
    to   { transform: scale(1)    translateY(0);    opacity: 1; }
  }
  /* Modal header bar */
  .am-bar {
    background: linear-gradient(135deg, var(--dark), var(--dark-s));
    padding: 28px 32px 20px; position: relative;
  }
  .am-bar-title { font-family: var(--font-d); font-size: 1.7rem; font-weight: 300; color: #fff; margin-bottom: 3px; }
  .am-bar-sub   { font-family: var(--font-b); font-size: 0.93rem; color: rgba(255,255,255,0.7); }
  .am-close {
    position: absolute; top: 14px; right: 18px;
    background: none; border: none; cursor: pointer;
    font-size: 1.3rem; color: rgba(255,255,255,0.72); line-height: 1; transition: color 0.2s;
  }
  .am-close:hover { color: #fff; }
  /* Method tabs inside modal */
  .am-tabs { display: flex; background: rgba(255,255,255,0.06); border-top: 1px solid rgba(255,255,255,0.07); }
  .am-tab {
    flex: 1; background: none; border: none; cursor: pointer;
    font-family: var(--font-b); font-size: 0.8rem; font-weight: 500;
    letter-spacing: 1.2px; text-transform: uppercase;
    color: rgba(255,255,255,0.38); padding: 12px 6px;
    border-bottom: 2px solid transparent; transition: all 0.2s; white-space: nowrap;
  }
  .am-tab:hover { color: rgba(255,255,255,0.72); }
  .am-tab.am-on { color: var(--gold-lt); border-bottom-color: var(--gold); }
  /* Modal body */
  .am-body { padding: 26px 32px 30px; }
  .am-back {
    background: none; border: none; cursor: pointer;
    font-family: var(--font-b); font-size: 0.97rem; color: #3D2E22;
    display: flex; align-items: center; gap: 5px;
    margin-bottom: 16px; padding: 0; transition: color 0.2s;
  }
  .am-back:hover { color: var(--dark); }
  /* Form labels */
  .am-label {
    display: block; font-family: var(--font-b); font-size: 0.8rem;
    font-weight: 500; letter-spacing: 1.5px; text-transform: uppercase;
    color: var(--brown); margin-bottom: 5px;
  }
  .am-req { color: var(--rose); margin-left: 2px; }
  .am-label-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 5px; }
  .am-label-row .am-label { margin-bottom: 0; }
  /* Login identifier toggle (Feature 1) */
  /* am-id-toggle removed — login uses single combined field */
  /* Inputs — plain JSX, NOT wrapped in sub-components */
  .am-input {
    display: block; width: 100%;
    background: var(--ivory); border: 1px solid var(--cream); border-radius: 2px;
    font-family: var(--font-b); font-size: 1.05rem; color: var(--dark);
    padding: 12px 15px; outline: none; margin-bottom: 13px;
    transition: border-color 0.2s, background 0.2s, box-shadow 0.2s;
    -webkit-appearance: none; appearance: none;
  }
  /* Password fields get larger dots */
  .am-input[type="password"] {
    font-size: 1.4rem; letter-spacing: 4px; padding: 9px 15px;
  }
  .am-input:focus { border-color: var(--gold); background: #fff; box-shadow: 0 0 0 3px rgba(201,168,76,0.1); }
  .am-input.am-err { border-color: var(--rose); }
  /* OTP row */
  .am-otp-row { display: flex; gap: 10px; align-items: stretch; margin-bottom: 13px; }
  .am-otp-row .am-input { margin-bottom: 0; flex: 1; min-width: 0; }
  .am-otp-btn {
    background: var(--gold); color: var(--dark); border: none; cursor: pointer;
    font-family: var(--font-b); font-size: 0.86rem; font-weight: 600;
    letter-spacing: 1px; padding: 0 14px; border-radius: 2px;
    flex-shrink: 0; white-space: nowrap; transition: opacity 0.2s;
  }
  .am-otp-btn:disabled { opacity: 0.5; cursor: not-allowed; }
  .am-cooldown { font-family: var(--font-b); font-size: 0.86rem; color: var(--brown-l); padding: 0 8px; align-self: center; white-space: nowrap; flex-shrink: 0; }
  .am-otp-info {
    background: var(--ivory); border: 1px solid var(--cream); border-radius: 2px;
    padding: 10px 14px; margin-bottom: 13px;
    font-family: var(--font-b); font-size: 0.8rem; color: var(--brown); line-height: 1.8;
  }
  /* Alerts */
  .am-error   { font-family: var(--font-b); font-size: 0.9rem; color: var(--rose); margin-bottom: 12px; padding: 9px 13px; background: rgba(196,115,106,0.08); border-radius: 2px; border-left: 3px solid var(--rose); }
  .am-success { font-family: var(--font-b); font-size: 0.96rem; color: #3A6035; margin-bottom: 12px; padding: 9px 13px; background: rgba(122,155,118,0.1); border-radius: 2px; border-left: 3px solid var(--sage); }
  /* Primary button */
  .am-btn {
    display: block; width: 100%;
    background: linear-gradient(135deg, var(--gold), var(--gold-lt));
    color: var(--dark); border: none; cursor: pointer;
    font-family: var(--font-b); font-size: 0.8rem; font-weight: 600;
    letter-spacing: 2px; text-transform: uppercase;
    padding: 14px; border-radius: 2px;
    box-shadow: 0 3px 16px rgba(201,168,76,0.35); transition: all 0.25s;
  }
  .am-btn:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 6px 22px rgba(201,168,76,0.5); }
  .am-btn:disabled { opacity: 0.55; cursor: not-allowed; }
  /* Divider */
  .am-divider { display: flex; align-items: center; gap: 12px; margin: 16px 0; }
  .am-divider::before, .am-divider::after { content: ''; flex: 1; height: 1px; background: var(--cream); }
  .am-divider span { font-family: var(--font-b); font-size: 0.96rem; color: var(--brown-l); letter-spacing: 1px; text-transform: uppercase; white-space: nowrap; }
  /* Alt method buttons */
  .am-alt-row { display: flex; gap: 10px; }
  .am-alt-btn { flex: 1; padding: 10px; border: 1px solid var(--cream); border-radius: 2px; background: var(--ivory); cursor: pointer; font-family: var(--font-b); font-size: 0.88rem; color: var(--brown); transition: border-color 0.2s; }
  .am-alt-btn:hover { border-color: var(--gold); }
  /* Switch row */
  .am-switch { text-align: center; margin-top: 18px; font-family: var(--font-b); font-size: 0.8rem; color: var(--brown-l); }
  .am-link { background: none; border: none; cursor: pointer; color: var(--gold); font-family: var(--font-b); font-size: 0.8rem; font-weight: 500; text-decoration: underline; padding: 0; }
  .am-link.sm { font-size: 0.88rem; }
  /* Spinner */
  .am-spinner { display: inline-block; width: 13px; height: 13px; border: 2px solid rgba(26,20,16,0.18); border-top-color: var(--dark); border-radius: 50%; margin-right: 8px; vertical-align: middle; animation: am-spin 0.7s linear infinite; }
  @keyframes am-spin { to { transform: rotate(360deg); } }
  /* Password strength */
  .am-pw-bar   { height: 3px; border-radius: 2px; margin: -8px 0 10px; transition: all 0.3s; }
  .am-pw-label { font-family: var(--font-b); font-size: 0.7rem; margin-bottom: 8px; margin-top: -6px; }
  /* Note box */
  .am-note { background: var(--ivory); border: 1px solid var(--cream); border-left: 3px solid var(--rose); border-radius: 2px; padding: 12px 14px; margin-top: 14px; font-family: var(--font-b); font-size: 0.94rem; color: var(--brown); line-height: 1.85; }
  /* Help text */
  .am-help { font-family: var(--font-b); font-size: 0.92rem; color: var(--brown-l); margin-top: 10px; text-align: center; }

  /* ══ GLOBAL TOAST ══ */
  .g-toast { position: fixed; bottom: 28px; right: 28px; z-index: 9999; background: var(--dark); color: #fff; border-radius: 3px; padding: 14px 22px; font-family: var(--font-b); font-size: 0.97rem; box-shadow: 0 8px 32px rgba(26,20,16,0.4); max-width: 320px; line-height: 1.5; animation: am-fadein 0.3s ease; }
  .g-toast.success { border-left: 3px solid var(--sage); }
  .g-toast.error   { border-left: 3px solid var(--rose); }
  .g-toast.info    { border-left: 3px solid var(--gold); }

  /* ══ ADMIN ACCESS GUARD ══ */
  .g-guard { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: calc(100vh - var(--gh-header-h)); text-align: center; padding: 40px; }
  .g-guard h2 { font-family: var(--font-d); font-size: 2rem; font-weight: 300; color: var(--gold); margin-bottom: 12px; }
  .g-guard p  { color: #7A6050; margin-bottom: 24px; font-size: 0.9rem; }
  .g-guard button { background: linear-gradient(135deg, var(--gold), var(--gold-lt)); border: none; color: var(--dark); padding: 12px 32px; font-family: var(--font-b); font-size: 0.8rem; font-weight: 600; letter-spacing: 2px; text-transform: uppercase; cursor: pointer; border-radius: 2px; }

  /* ══ RESPONSIVE ══ */
  @media (max-width: 768px) {
    .gh-header { padding: 0 16px; }
    .gh-logo-sub { display: none; }
    .gh-btn:not(.gh-btn-gold) { font-size: 0.96rem; padding: 7px 9px; letter-spacing: 1px; }
    .am-modal { max-width: 100%; border-radius: 4px; }
    .am-body  { padding: 20px 20px 24px; }
    .am-bar   { padding: 20px 20px 16px; }
  }
  @media (max-width: 420px) {
    .gh-sep { display: none; }
  }
`;

// ── Password strength helper ────────────────────────────────────
function getPasswordStrength(pw) {
  if (!pw) return { pct: 0, color: 'transparent', label: '' };
  let s = 0;
  if (pw.length >= 6)           s++;
  if (pw.length >= 10)          s++;
  if (/[A-Z]/.test(pw))        s++;
  if (/[0-9]/.test(pw))        s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  const levels = [
    { pct: 0,   color: 'transparent', label: '' },
    { pct: 20,  color: '#C4736A',     label: 'Weak' },
    { pct: 40,  color: '#E8A030',     label: 'Fair' },
    { pct: 60,  color: '#C9A84C',     label: 'Good' },
    { pct: 80,  color: '#7A9B76',     label: 'Strong' },
    { pct: 100, color: '#3A7A3A',     label: 'Very Strong' },
  ];
  return levels[Math.min(s, 5)];
}

// ══════════════════════════════════════════════════════════════
//   AUTH MODAL
//   All inputs rendered as plain JSX — no sub-components.
//   This prevents focus loss on every keystroke.
//
//   Feature 1: Login mode has Email / Mobile toggle
//   Feature 2: Registration error messages are specific
//              (backend returns "email already registered" vs
//               "mobile number already registered")
// ══════════════════════════════════════════════════════════════
function AuthModal({ initialMode, onClose, onSuccess }) {
  // Mode: 'login' | 'register' | 'email-otp' | 'mobile-otp' | 'otp' | 'forgot' | 'change-password' | 'reset-password'
  const [mode,     setMode]     = useState(initialMode || 'login');
  // Feature 1: login can be by email or phone
  // loginBy state removed — single field now handles both email and phone
  // Form fields
  const [name,     setName]     = useState('');
  const [email,    setEmail]    = useState('');
  const [phone,    setPhone]    = useState('');
  const [password, setPassword] = useState('');
  const [confirmPw,setConfirmPw]= useState('');
  const [otp,      setOtp]      = useState('');
  const [token,    setToken]    = useState('');
  // Unified OTP target (email or phone — detected automatically)
  const [otpTarget, setOtpTarget] = useState(''); // what user typed
  const [otpType,   setOtpType]   = useState(''); // 'email' | 'phone' — detected
  // UI state
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState('');
  const [success,  setSuccess]  = useState('');
  const [otpSent,  setOtpSent]  = useState(false);
  const [cooldown, setCooldown] = useState(0);

  const pws = getPasswordStrength(password);

  // Auto-fill token + email from URL params when opened via reset link
  useEffect(() => {
    if ((initialMode === 'reset-password' || mode === 'reset-password') && window._absResetToken) {
      setToken(window._absResetToken);
      setEmail(window._absResetEmail || '');
      window._absResetToken = null;
      window._absResetEmail = null;
    }
  }, [mode, initialMode]); // eslint-disable-line

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown(c => c - 1), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const switchMode = (m) => {
    setMode(m); setError(''); setSuccess('');
    setOtp(''); setOtpSent(false); setCooldown(0);
    setPassword(''); setConfirmPw(''); setToken('');
  };

  // Stable onChange handlers — defined at component level, never re-created
  const onEmail    = (e) => { setEmail(e.target.value);    setError(''); };
  const onName     = (e) => { setName(e.target.value);     setError(''); };
  const onPhone    = (e) => { setPhone(e.target.value);    setError(''); };
  const onPassword = (e) => { setPassword(e.target.value); setError(''); };
  const onConfirm  = (e) => { setConfirmPw(e.target.value);setError(''); };
  const onOtp      = (e) => { setOtp(e.target.value);      setError(''); };
  const onToken    = (e) => { setToken(e.target.value);    setError(''); };

  // ── Login: single field accepts email OR phone ────────────
  const handleLogin = async () => {
    if (!email.trim()) { setError('Email or mobile number is required.'); return; }
    if (!password) { setError('Password is required.'); return; }
    setLoading(true); setError('');
    try {
      // Detect if input is a phone number (contains only digits, +, spaces)
      const raw     = email.trim();
      const isPhone = /^[+\d\s\-()]{7,}$/.test(raw) && !raw.includes('@');
      const payload = { password };
      if (isPhone) payload.phone = raw;
      else         payload.email = raw.toLowerCase();
      const res = await axios.post(`${API_URL}/auth/login`, payload);
      localStorage.setItem('abs_token', res.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      onSuccess(res.data.user, `✓ Welcome back, ${res.data.user.name}!`, 'success');
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid credentials. Please try again.');
    } finally { setLoading(false); }
  };

  // ── Feature 2: Register — backend returns specific duplicate errors ──
  const handleRegister = async () => {
    if (!name.trim())          { setError('Full name is required.'); return; }
    if (!email.trim())         { setError('Email address is required.'); return; }
    if (!phone.trim())         { setError('Phone number is required.'); return; }
    if (password.length < 6)   { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirmPw){ setError('Passwords do not match.'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/auth/register`, {
        name: name.trim(), email: email.trim().toLowerCase(),
        phone: phone.trim(), password,
      });
      localStorage.setItem('abs_token', res.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      onSuccess(res.data.user, `✓ Welcome, ${res.data.user.name}! Account created.`, 'success');
      onClose();
    } catch (err) {
      // Backend sends distinct messages: "email already registered" or "mobile number already registered"
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  // Detect if input is email or phone
  const detectType = (val) => {
    const v = val.trim();
    if (!v) return '';
    if (v.includes('@')) return 'email';
    if (/^[+\d\s\-()]{7,}$/.test(v)) return 'phone';
    return 'email'; // default to email for ambiguous
  };

  const sendOTP = async (overrideTarget) => {
    const raw    = (overrideTarget || otpTarget).trim();
    const type   = detectType(raw);
    const target = type === 'phone' ? raw.replace(/\D/g,'').slice(-10) : raw.toLowerCase();
    if (!target) { setError('Enter your email or mobile number first.'); return; }
    if (!type)   { setError('Enter a valid email or mobile number.'); return; }
    setOtpType(type);
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/auth/send-otp`, { type, target, purpose: 'login' });
      setOtpSent(true); setCooldown(60);
      setSuccess(type === 'phone'
        ? `OTP sent to ${raw} via SMS and WhatsApp.`
        : `OTP sent to ${raw}. Check your inbox.`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send OTP. Please try again.');
    } finally { setLoading(false); }
  };

  const verifyOTP = async () => {
    const raw    = otpTarget.trim();
    const type   = otpType || detectType(raw);
    const target = type === 'phone' ? raw.replace(/\D/g,'').slice(-10) : raw.toLowerCase();
    if (!otp.trim() || otp.trim().length !== 6) { setError('Please enter the 6-digit OTP.'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/auth/verify-otp`, {
        type, target, otp: otp.trim(), purpose: 'login',
      });
      localStorage.setItem('abs_token', res.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      onSuccess(res.data.user, `✓ Welcome${res.data.user?.name ? `, ${res.data.user.name}` : ''}!`, 'success');
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired OTP.');
    } finally { setLoading(false); }
  };

  // eslint-disable-next-line no-unused-vars
  const handleChangePassword = async () => {
    if (!password)           { setError('Current password is required.'); return; }
    if (!confirmPw)          { setError('New password is required.'); return; }
    if (confirmPw.length < 6){ setError('New password must be at least 6 characters.'); return; }
    if (password === confirmPw){ setError('New password must be different from the current one.'); return; }
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('abs_token');
      await axios.patch(`${API_URL}/auth/change-password`,
        { currentPassword: password, newPassword: confirmPw },
        { headers:{ Authorization:`Bearer ${token}` } }
      );
      setSuccess('✓ Password changed successfully!');
      setPassword(''); setConfirmPw('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password. Please try again.');
    } finally { setLoading(false); }
  };

  const handleForgot = async () => {
    if (!email.trim()) { setError('Email address is required.'); return; }
    setLoading(true); setError('');
    try {
      await axios.post(`${API_URL}/auth/forgot-password`, { email: email.trim().toLowerCase() });
      setSuccess('✓ If this email is registered, a reset link has been sent. Check your inbox.');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send reset email.');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async () => {
    if (!token.trim())         { setError('Reset token is required.'); return; }
    if (password.length < 6)   { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirmPw){ setError('Passwords do not match.'); return; }
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API_URL}/auth/reset-password`, {
        email: email.trim().toLowerCase(), token: token.trim(), password,
      });
      localStorage.setItem('abs_token', res.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      onSuccess(res.data.user, '✓ Password reset! You are now signed in.', 'success');
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired token. Request a new one.');
    } finally { setLoading(false); }
  };

  const TITLES = {
    'login':          ['Welcome Back',      'Sign in to your account'],
    'register':       ['Create Account',    "Join ABS Dress Designer — it's free"],
    'email-otp':      ['Sign In with OTP',  'Enter your email or mobile number'],
    'mobile-otp':     ['Sign In with OTP',  'Enter your email or mobile number'],
    'otp':            ['Sign In with OTP',  'Enter your email or mobile — we detect automatically'],
    'forgot':         ['Forgot Password',   "We'll email you a reset link"],
    'change-password':['Change Password',  'Enter your current and new password'],
    'reset-password': ['Set New Password',  'Enter the token from your email'],
  };
  const [title, sub] = TITLES[mode] || TITLES['login'];

  return (
    <div className="am-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="am-modal">

        {/* Modal header bar */}
        <div className="am-bar">
          <button className="am-close" onClick={onClose} aria-label="Close">✕</button>
          <div className="am-bar-title">{title}</div>
          <div className="am-bar-sub">{sub}</div>
        </div>

        {/* Method tabs — only on login / OTP modes */}
        {['login','email-otp','mobile-otp','otp'].includes(mode) && (
          <div className="am-tabs">
            <button className={`am-tab${mode === 'login' ? ' am-on' : ''}`} onClick={() => switchMode('login')}>🔑 Password</button>
            <button className={`am-tab${['email-otp','mobile-otp','otp'].includes(mode) ? ' am-on' : ''}`} onClick={() => { switchMode('otp'); setOtpTarget(''); setOtpType(''); }}>✉️ OTP Login</button>
          </div>
        )}

        <div className="am-body">
          {['forgot','reset-password'].includes(mode) && (
            <button className="am-back" onClick={() => switchMode('login')}>← Back to Sign In</button>
          )}
          {error   && <div className="am-error">⚠ {error}</div>}
          {success && <div className="am-success">{success}</div>}

          {/* ══ MODE: login ══ */}
          {mode === 'login' && (
            <>
                  {/* Single field: accepts email OR mobile number */}
              <label className="am-label">Email or Mobile Number <span className="am-req">*</span></label>
              <input className={`am-input${error && !email.trim() ? ' am-err' : ''}`}
                type="text" placeholder="your@email.com or +91 98765 43210"
                value={email} onChange={onEmail}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                autoComplete="username" autoFocus />

              <div className="am-label-row">
                <label className="am-label">Password <span className="am-req">*</span></label>
                <button className="am-link sm" onClick={() => switchMode('forgot')}>Forgot password?</button>
              </div>
              <input className={`am-input${error && !password ? ' am-err' : ''}`}
                type="password" placeholder="••••••••"
                value={password} onChange={onPassword}
                onKeyDown={e => e.key === 'Enter' && handleLogin()}
                autoComplete="current-password" style={{ marginBottom: 22 }} />

              <button className="am-btn" onClick={handleLogin} disabled={loading}>
                {loading && <span className="am-spinner" />}
                {loading ? 'Signing in...' : 'Sign In'}
              </button>
              <div className="am-divider"><span>or sign in with</span></div>
              <div className="am-alt-row">
                <button className="am-alt-btn" onClick={() => { switchMode('otp'); setOtpTarget(''); setOtpType(''); }}>✉️ OTP Login</button>
                <button className="am-alt-btn" onClick={() => switchMode('forgot')}>🔑 Reset Password</button>
              </div>
              <div className="am-switch">
                Don't have an account?{' '}
                <button className="am-link" onClick={() => switchMode('register')}>Register free</button>
              </div>
            </>
          )}

          {/* ══ MODE: register ══ */}
          {mode === 'register' && (
            <>
              <label className="am-label">Full Name <span className="am-req">*</span></label>
              <input className={`am-input${error && !name.trim() ? ' am-err' : ''}`}
                type="text" placeholder="e.g. Priya Sundaram"
                value={name} onChange={onName} autoComplete="name" autoFocus />

              <label className="am-label">Email Address <span className="am-req">*</span></label>
              <input className={`am-input${error && (error.toLowerCase().includes('email') || !email.trim()) ? ' am-err' : ''}`}
                type="email" placeholder="your@email.com"
                value={email} onChange={onEmail} autoComplete="email" />

              <label className="am-label">Phone Number <span className="am-req">*</span></label>
              <input className={`am-input${error && (error.toLowerCase().includes('mobile') || !phone.trim()) ? ' am-err' : ''}`}
                type="tel" placeholder="+91 98765 43210"
                value={phone} onChange={onPhone} autoComplete="tel" />

              <label className="am-label">Password <span className="am-req">*</span></label>
              <input className={`am-input${error && !password ? ' am-err' : ''}`}
                type="password" placeholder="Min. 6 characters"
                value={password} onChange={onPassword} autoComplete="new-password" />
              {password && (
                <>
                  <div className="am-pw-bar" style={{ width: `${pws.pct}%`, background: pws.color, minWidth: '8%' }} />
                  {pws.label && <p className="am-pw-label" style={{ color: pws.color }}>Strength: {pws.label}</p>}
                </>
              )}

              <label className="am-label">Confirm Password <span className="am-req">*</span></label>
              <input className={`am-input${error && password !== confirmPw ? ' am-err' : ''}`}
                type="password" placeholder="Repeat password"
                value={confirmPw} onChange={onConfirm}
                onKeyDown={e => e.key === 'Enter' && handleRegister()}
                autoComplete="new-password" style={{ marginBottom: 22 }} />

              <button className="am-btn" onClick={handleRegister} disabled={loading}>
                {loading && <span className="am-spinner" />}
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
              <div className="am-switch">
                Already have an account?{' '}
                <button className="am-link" onClick={() => switchMode('login')}>Sign In</button>
              </div>
            </>
          )}

          {/* ══ MODE: otp (unified — email or mobile, auto-detected) ══ */}
          {['email-otp','mobile-otp','otp'].includes(mode) && (
            <>
              <label className="am-label">Email or Mobile Number <span className="am-req">*</span></label>
              <input className={`am-input${error && !otpTarget.trim() ? ' am-err' : ''}`}
                type="text" placeholder="your@email.com  or  98765 43210"
                value={otpTarget}
                onChange={e => { setOtpTarget(e.target.value); setError(''); setOtpSent(false); setOtpType(''); }}
                autoComplete="username" autoFocus
                disabled={otpSent} />
              {otpTarget.trim() && !otpSent && (
                <div style={{fontFamily:"var(--font-b)",fontSize:'.82rem',color:'rgba(255,255,255,.45)',marginBottom:8}}>
                  {detectType((otpTarget||email).trim()) === 'phone'
                    ? '📱 OTP will be sent via SMS + WhatsApp'
                    : detectType((otpTarget||email).trim()) === 'email'
                    ? '📧 OTP will be sent to this email'
                    : ''}
                </div>
              )}

              {!otpSent ? (
                <>
                  <button className="am-btn" onClick={() => sendOTP(otpTarget||email)} disabled={loading || !(otpTarget||email).trim()}>
                    {loading && <span className="am-spinner" />}
                    {loading ? 'Sending...' : 'Send OTP →'}
                  </button>
                  <p className="am-help">Enter your email address or mobile number — we'll detect it automatically and send the OTP.</p>
                </>
              ) : (
                <>
                  <div className="am-otp-info" style={{marginBottom:10}}>
                    {otpType === 'phone'
                      ? <>OTP sent to <strong>{otpTarget||email}</strong> via SMS and WhatsApp. Valid 10 minutes.</>
                      : <>OTP sent to <strong>{otpTarget||email}</strong>. Check your inbox. Valid 10 minutes.</>}
                  </div>
                  <label className="am-label">One-Time Password</label>
                  <div className="am-otp-row">
                    <input className={`am-input${error && !otp ? ' am-err' : ''}`}
                      type="text" inputMode="numeric" placeholder="6-digit OTP"
                      maxLength={6} value={otp} onChange={onOtp}
                      onKeyDown={e => e.key === 'Enter' && verifyOTP()}
                      autoComplete="one-time-code" autoFocus />
                    {cooldown > 0
                      ? <span className="am-cooldown">Resend in {cooldown}s</span>
                      : <button className="am-otp-btn" onClick={() => { setOtpSent(false); setOtp(''); setSuccess(''); }} disabled={loading}>Change</button>
                    }
                  </div>
                  <button className="am-btn" onClick={() => verifyOTP()} disabled={loading || otp.length !== 6}>
                    {loading && <span className="am-spinner" />}
                    {loading ? 'Verifying...' : 'Verify OTP & Sign In'}
                  </button>
                </>
              )}

              <div className="am-switch">
                <button className="am-link" onClick={() => switchMode('login')}>Use password instead</button>
                {' · '}
                <button className="am-link" onClick={() => switchMode('forgot')}>Reset password</button>
                {' · '}
                <button className="am-link" onClick={() => switchMode('register')}>Register</button>
              </div>
            </>
          )}

          {/* ══ MODE: forgot ══ */}
          {mode === 'forgot' && (
            <>
              <p style={{ fontFamily: 'var(--font-b)', fontSize: '.84rem', color: '#4A3728', lineHeight: 1.8, marginBottom: 16 }}>
                Enter your registered email. We'll send a password reset link valid for <strong>48 hours</strong>.<br/>
                <span style={{color:'#8A7060',fontSize:'.82rem'}}>Already signed in? Use <strong>My Dashboard → Change Password</strong> instead.</span>
              </p>
              <label className="am-label">Email Address <span className="am-req">*</span></label>
              <input className={`am-input${error && !email.trim() ? ' am-err' : ''}`}
                type="email" placeholder="your@email.com"
                value={email} onChange={onEmail}
                onKeyDown={e => e.key === 'Enter' && handleForgot()}
                autoComplete="email" autoFocus />
              <button className="am-btn" onClick={handleForgot}
                disabled={loading || !!success} style={{ marginTop: 4 }}>
                {loading && <span className="am-spinner" />}
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
              {success && (
                <>
                  <div className="am-divider"><span>already have the token?</span></div>
                  <button className="am-btn"
                    style={{ background: 'linear-gradient(135deg,#2C2318,#1A1410)', color: '#E8C96D' }}
                    onClick={() => { setSuccess(''); switchMode('reset-password'); }}>
                    Enter Token & Set Password →
                  </button>
                </>
              )}
              <div className="am-switch">
                Remember your password?{' '}
                <button className="am-link" onClick={() => switchMode('login')}>Sign In</button>
              </div>
            </>
          )}



          {/* ══ MODE: reset-password ══ */}
          {mode === 'reset-password' && (
            <>
              {token ? (
                <div style={{background:'rgba(91,173,111,.08)',border:'1px solid rgba(91,173,111,.25)',borderRadius:4,padding:'10px 14px',fontFamily:'var(--font-b)',fontSize:'.85rem',color:'#2D6A35',marginBottom:16}}>
                  ✅ Reset link verified — just enter your new password below.
                </div>
              ) : (
                <p style={{ fontFamily: 'var(--font-b)', fontSize: '.82rem', color: '#4A3728', lineHeight: 1.8, marginBottom: 18 }}>
                  Click the <strong>Reset Password</strong> button in the email we sent you — it will auto-fill everything here. Or paste the token manually below.
                </p>
              )}

              <label className="am-label">Email Address <span className="am-req">*</span></label>
              <input className={`am-input${error && !email.trim() ? ' am-err' : ''}`}
                type="email" placeholder="your@email.com"
                value={email} onChange={onEmail} autoComplete="email" />

              {!token && (
                <>
                  <label className="am-label">Reset Token <span className="am-req">*</span></label>
                  <input className={`am-input${error && !token.trim() ? ' am-err' : ''}`}
                    type="text" placeholder="Paste token from your email"
                    value={token} onChange={onToken} />
                </>
              )}

              <label className="am-label">New Password <span className="am-req">*</span></label>
              <input className={`am-input${error && !password ? ' am-err' : ''}`}
                type="password" placeholder="Min. 6 characters"
                value={password} onChange={onPassword} autoComplete="new-password" autoFocus={!!token} />
              {password && (
                <>
                  <div className="am-pw-bar" style={{ width: `${pws.pct}%`, background: pws.color, minWidth: '8%' }} />
                  {pws.label && <p className="am-pw-label" style={{ color: pws.color }}>Strength: {pws.label}</p>}
                </>
              )}

              <label className="am-label">Confirm New Password <span className="am-req">*</span></label>
              <input className={`am-input${error && password !== confirmPw ? ' am-err' : ''}`}
                type="password" placeholder="Repeat new password"
                value={confirmPw} onChange={onConfirm}
                onKeyDown={e => e.key === 'Enter' && handleResetPassword()}
                autoComplete="new-password" style={{ marginBottom: 22 }} />

              <button className="am-btn" onClick={handleResetPassword} disabled={loading}>
                {loading && <span className="am-spinner" />}
                {loading ? 'Setting password...' : 'Set Password & Sign In'}
              </button>
              <div style={{ textAlign: 'center', marginTop: 12 }}>
                <button className="am-link sm" onClick={() => switchMode('forgot')}>Resend reset email</button>
              </div>
              <div className="am-note">⚠ Didn't get the email? Check spam folder or request a new reset link.</div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
//   GLOBAL HEADER
// ══════════════════════════════════════════════════════════════
function GlobalHeader({ page, setPage, user, loyaltyBalance, savedCartCount, onLogout, onShowAuth }) {
  const [openMenu, setOpenMenu] = React.useState(null);
  // The header's height is no longer a fixed 70px — on narrower screens
  // or for accounts with lots of nav items (cart badge + loyalty points +
  // admin link + user pill all at once), the nav row wraps onto a second
  // line and the header genuinely needs to be taller. Previously the
  // page content below always assumed exactly 70px of clearance
  // (.gh-page { padding-top:70px }), so a wrapped header would overlap
  // the page content underneath it. This measures the header's ACTUAL
  // rendered height and publishes it as a CSS variable so every page's
  // top padding can stay correct no matter how many lines the nav wraps to.
  const headerRef = React.useRef(null);
  React.useEffect(() => {
    const el = headerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const updateHeight = () => {
      document.documentElement.style.setProperty('--gh-header-h', `${el.offsetHeight}px`);
    };
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return (
    <header className="gh-header" ref={headerRef}>
      <div className="gh-logo" onClick={() => setPage(user?.role === 'payment_team' ? 'admin' : 'home')}>
        <div className="gh-logo-badge">ABS</div>
        <div>
          <span className="gh-logo-name">ABS DRESS DESIGNER</span>
          <span className="gh-logo-sub">Couture Stitching Studio</span>
        </div>
      </div>
      <nav className="gh-nav">
        {user?.role !== 'payment_team' && (
        <button className={`gh-btn${page === 'home' || page === 'dashboard' ? ' gh-active' : ''}`}
          onClick={() => setPage(user && user.role !== 'admin' ? 'dashboard' : 'home')}>
          {user && user.role !== 'admin' ? '🏠 My Dashboard' : 'Home'}
        </button>
        )}

        {/* Saved cart badge — ONLY shown when a non-empty cart exists in
            localStorage (see App.js checkSavedCart). Hidden entirely
            otherwise, per design: no empty/placeholder state in the nav. */}
        {savedCartCount > 0 && (
          <button className="gh-btn gh-cart-pill" onClick={() => setPage('order')}>
            🛍 Saved Cart <span className="gh-cart-count">{savedCartCount}</span>
          </button>
        )}

        {user?.role !== 'payment_team' && (
        <>
        {/* Services dropdown */}
        <div className={`gh-menu${openMenu==='services'?' open':''}`}
          onMouseEnter={()=>setOpenMenu('services')} onMouseLeave={()=>setOpenMenu(null)}>
          <button className={`gh-menu-btn${['order','track'].includes(page)?' gh-active':''}`}>Services</button>
          <div className="gh-dropdown"><div className="gh-dropdown-inner">
            <button className={`gh-dd-item${page==='order'?' active':''}`} onClick={()=>{setPage('order');setOpenMenu(null);}}>✂️ Place Order</button>
            <button className="gh-dd-item" onClick={()=>{
              setOpenMenu(null);
              if (page === 'home') {
                // Already on home — scroll to Quick Order section
                setTimeout(() => {
                  const el = document.getElementById('quick-order-section');
                  if (el) el.scrollIntoView({ behavior:'smooth', block:'start' });
                }, 50);
              } else {
                // Navigate home then scroll
                setPage('home');
                setTimeout(() => {
                  const el = document.getElementById('quick-order-section');
                  if (el) el.scrollIntoView({ behavior:'smooth', block:'start' });
                }, 400);
              }
            }}>⚡ Quick Order</button>
            <button className={`gh-dd-item${page==='track'?' active':''}`} onClick={()=>{setPage('track');setOpenMenu(null);}}>📦 Track Order</button>
          </div></div>
        </div>

        {/* Explore dropdown */}
        <div className={`gh-menu${openMenu==='explore'?' open':''}`}
          onMouseEnter={()=>setOpenMenu('explore')} onMouseLeave={()=>setOpenMenu(null)}>
          <button className={`gh-menu-btn${['gallery'].includes(page)?' gh-active':''}`}>Explore</button>
          <div className="gh-dropdown"><div className="gh-dropdown-inner">
            <button className={`gh-dd-item${page==='gallery'?' active':''}`} onClick={()=>{setPage('gallery');setOpenMenu(null);}}>🖼 Portfolio Gallery</button>
            <button className={`gh-dd-item${page==='style'?' active':''}`} onClick={()=>{setPage('style');setOpenMenu(null);}}>✨ Style Finder</button>
            <button className={`gh-dd-item${page==='measurements'?' active':''}`} onClick={()=>{setPage('measurements');setOpenMenu(null);}}>📐 Measurement Guide</button>
            <button className={`gh-dd-item${page==='faq'?' active':''}`} onClick={()=>{setPage('faq');setOpenMenu(null);}}>❓ FAQ</button>
            <div style={{height:'1px',background:'rgba(201,168,76,.1)',margin:'3px 0'}}/>
            <button className={`gh-dd-item${page==='ai-measure'?' active':''}`} onClick={()=>{setPage('ai-measure');setOpenMenu(null);}}>🤖 AI Measurement Advisor</button>
            <button className={`gh-dd-item${page==='ai-style'?' active':''}`} onClick={()=>{setPage('ai-style');setOpenMenu(null);}}>✨ AI Styling Advisor</button>
            <button className={`gh-dd-item${page==='multilang'?' active':''}`} onClick={()=>{setPage('multilang');setOpenMenu(null);}}>🌐 Multi-Language</button>
          </div></div>
        </div>

        {/* Company dropdown */}
        <div className={`gh-menu${openMenu==='company'?' open':''}`}
          onMouseEnter={()=>setOpenMenu('company')} onMouseLeave={()=>setOpenMenu(null)}>
          <button className={`gh-menu-btn${['about','partner','terms'].includes(page)?' gh-active':''}`}>Company</button>
          <div className="gh-dropdown"><div className="gh-dropdown-inner">
            <button className={`gh-dd-item${page==='about'?' active':''}`} onClick={()=>{setPage('about');setOpenMenu(null);}}>✦ About Us</button>
            <button className={`gh-dd-item${page==='partner'?' active':''}`} onClick={()=>{setPage('partner');setOpenMenu(null);}}>🤝 Become a Partner</button>
            <button className={`gh-dd-item${page==='terms'?' active':''}`} onClick={()=>{setPage('terms');setOpenMenu(null);}}>📋 Terms &amp; Policy</button>
          </div></div>
        </div>
        </>
        )}
        {(user?.role === 'admin' || user?.role === 'payment_team') && (
          <>
            <div className="gh-sep" />
            <button className={`gh-btn${page === 'admin' ? ' gh-active' : ''}`}
              style={{ color: 'rgba(201,168,76,0.72)' }}
              onClick={() => setPage('admin')}>
              {user.role === 'payment_team' ? '💰 Payments' : '⚙ Admin'}
            </button>
          </>
        )}
        <div className="gh-sep" />
        {user ? (
          <>
            <div className="gh-user-pill">
              <div className="gh-user-av">{(user.name || 'U')[0].toUpperCase()}</div>
              {user.name?.split(' ')[0] || 'Account'}
            </div>
            {loyaltyBalance !== null && user.role !== 'admin' && user.role !== 'payment_team' && (
              <div style={{
                fontFamily:"'Jost',sans-serif", fontSize:'.7rem', fontWeight:600,
                background:'linear-gradient(135deg,rgba(201,168,76,.15),rgba(232,201,109,.1))',
                border:'1px solid rgba(201,168,76,.35)', color:'#7A6000',
                padding:'4px 10px', borderRadius:50, whiteSpace:'nowrap',
                display:'flex', alignItems:'center', gap:4, letterSpacing:'.3px',
              }}>
                💎 {loyaltyBalance.toLocaleString()} pts
              </div>
            )}
            <button className="gh-btn" onClick={onLogout}>Sign Out</button>
          </>
        ) : (
          <>
            <button className="gh-btn" onClick={() => onShowAuth('login')}>Sign In</button>
            <button className="gh-btn gh-btn-gold" onClick={() => onShowAuth('register')}>Register</button>
          </>
        )}
      </nav>
    </header>
  );
}

// ══════════════════════════════════════════════════════════════
//   GLOBAL TOAST
// ══════════════════════════════════════════════════════════════
function GlobalToast({ toast, onDismiss }) {
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(onDismiss, 4500);
    return () => clearTimeout(t);
  }, [toast, onDismiss]);
  if (!toast) return null;
  return <div className={`g-toast${toast.type ? ` ${toast.type}` : ''}`}>{toast.message}</div>;
}

// ══════════════════════════════════════════════════════════════
//   ROOT APP
// ══════════════════════════════════════════════════════════════
// Memoized nav — only re-renders when page, user, or loyalty changes (not on toast/modal)
const MemoHeader = React.memo(GlobalHeader);

export default function App() {
  // Page state is initialised from the URL so deep links / refresh work.
  const [page,          setPageState]     = useState(() => pathToPage(window.location.pathname));
  // Navigating also pushes the matching URL so the address bar reflects the page.
  const setPage = useCallback((target) => {
    const path = pageToPath(target);
    if (window.location.pathname !== path) {
      window.history.pushState({ page: target }, '', path);
    }
    setPageState(target);
  }, []);
  // Keep page in sync when the user hits browser back/forward.
  useEffect(() => {
    const onPop = () => setPageState(pathToPage(window.location.pathname));
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  const [user,          setUser]          = useState(null);
  const [authLoading,   setAuthLoading]   = useState(true);
  const [showAuth,      setShowAuth]      = useState(null);
  const [toast,         setToast]         = useState(null);
  const [loyaltyBalance,setLoyaltyBalance]= useState(null);  // points balance for logged-in customer

  // ── Saved Cart Detection (header badge) ─────────────────────────
  // Mirrors OrderForm.jsx's persisted-cart feature: shows a "🛍 Saved
  // Cart" pill in the header ONLY when a non-empty cart actually exists
  // in localStorage, linking straight to the order page to resume it.
  // Hidden entirely otherwise — no empty/placeholder state in the nav.
  const CART_STORAGE_KEY = 'abs_order_cart_v1';
  const [savedCartCount, setSavedCartCount] = useState(0);

  const checkSavedCart = useCallback(() => {
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) { setSavedCartCount(0); return; }
      const parsed = JSON.parse(raw);
      setSavedCartCount(Array.isArray(parsed?.cartItems) ? parsed.cartItems.length : 0);
    } catch { setSavedCartCount(0); }
  }, []);

  useEffect(() => {
    checkSavedCart();
    // Cross-tab updates (native storage event — fires in OTHER tabs only)
    window.addEventListener('storage', checkSavedCart);
    // Same-tab updates — OrderForm.jsx dispatches this custom event
    // whenever it writes/clears the cart, since the native 'storage'
    // event never fires in the same tab/window that made the change.
    window.addEventListener('abs:cartUpdated', checkSavedCart);
    return () => {
      window.removeEventListener('storage', checkSavedCart);
      window.removeEventListener('abs:cartUpdated', checkSavedCart);
    };
  }, [checkSavedCart]);

  const showToast = useCallback((message, type = 'info') => setToast({ message, type }), []);

  // Load loyalty balance for a user
  const loadLoyaltyBalance = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/loyalty`);
      setLoyaltyBalance(res.data.points?.balance ?? null);
    } catch { setLoyaltyBalance(null); }
  }, []);

  // Restore saved session on mount
  useEffect(() => {
    const token = localStorage.getItem('abs_token');
    if (!token) { setAuthLoading(false); return; }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.get(`${API_URL}/auth/me`)
      .then(res => {
        const u = res.data.user || res.data;
        setUser(u);
        // On a fresh load of the ROOT url, send the user to their default
        // landing page. If they deep-linked to a specific page (e.g. /order),
        // respect that URL instead of bouncing them to the dashboard.
        const onRoot = window.location.pathname === '/';
        if (u?.role === 'admin' || u?.role === 'payment_team') {
          if (onRoot) setPage('admin');
        } else {
          if (onRoot) setPage('dashboard');
          axios.get(`${API_URL}/loyalty`)
            .then(r => setLoyaltyBalance(r.data.points?.balance ?? null))
            .catch(() => {});
        }
      })
      .catch(() => {
        localStorage.removeItem('abs_token');
        delete axios.defaults.headers.common['Authorization'];
      })
      .finally(() => setAuthLoading(false));
  }, []);

  // Auto-open reset modal if URL has ?token=&email=
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('token') && p.get('email')) {
      setShowAuth('reset-password');
      // Pre-fill token and email from URL so user doesn't have to paste anything
      // These are passed to the AuthModal via a ref/event workaround
      window._absResetToken = p.get('token');
      window._absResetEmail = p.get('email');
      // Clean up URL bar
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Listen for CustomerDashboard → "Reset via email" event
  useEffect(() => {
    const handler = (e) => setShowAuth(e.detail || 'forgot');
    window.addEventListener('abs:showAuth', handler);
    return () => window.removeEventListener('abs:showAuth', handler);
  }, []);

  const handleAuthSuccess = useCallback((userData, message, type) => {
    setUser(userData); showToast(message, type);
    // Redirect instantly to the right page on login
    if (userData?.role === 'admin' || userData?.role === 'payment_team') {
      setPage('admin');
    } else {
      setPage('dashboard');
      // Load loyalty balance for customers
      axios.get(`${API_URL}/loyalty`)
        .then(r => setLoyaltyBalance(r.data.points?.balance ?? null))
        .catch(() => {});
    }
  }, [showToast]); // API_URL is a module-level constant, not a reactive dep

  const handleLogout = useCallback(async () => {
    try { await axios.post(`${API_URL}/auth/logout`); } catch { /* ignore */ }
    localStorage.removeItem('abs_token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null); setPage('home'); setLoyaltyBalance(null);
    showToast('You have been signed out.', 'info');
  }, [showToast]);

  // ── SEO: update page title on navigation (must be before any early returns) ──
  React.useEffect(() => {
    const titles = {
      home:         'ABS Dress Designer — Premium Tailoring Studio Chennai',
      dashboard:    'My Dashboard — ABS Dress Designer',
      order:        'Place Order — Custom Stitching | ABS Dress Designer',
      track:        'Track Your Order — ABS Dress Designer',
      gallery:      'Portfolio Gallery — ABS Dress Designer',
      about:        'About Us — ABS Dress Designer',
      style:        'Style Finder — ABS Dress Designer',
      faq:          'FAQ — ABS Dress Designer',
      measurements: 'Measurement Guide — ABS Dress Designer',
      partner:      'Become a Partner — ABS Dress Designer',
      terms:        'Terms & Policy — ABS Dress Designer',
      admin:        'Admin Dashboard — ABS Dress Designer',
    };
    document.title = titles[page] || 'ABS Dress Designer';
  }, [page]);

  const contextValue = { page, setPage, user, setUser, showToast, API_URL, loyaltyBalance, setLoyaltyBalance, loadLoyaltyBalance };

  // Memoized — only re-evaluates when page or user changes, not on every toast/state update
  const renderPage = React.useMemo(() => {
    switch (page) {
      case 'dashboard': return user && user.role !== 'admin' ? <CustomerDashboard /> : <ABSDressDesigner />;
      case 'order': return <OrderForm />;
      case 'track':   return <OrderTracking />;
      case 'gallery':  return <PortfolioGallery />;
      case 'about':    return <AboutUs />;
      case 'terms':    return <TermsPolicy />;
      case 'style':    return <StyleGuide />;
      case 'faq':      return <FAQPage />;
      case 'measurements': return <MeasurementGuide />;
      case 'partner':  return <BecomePartner />;
      case 'ai-measure':  return <AIMeasurementAdvisor />;
      case 'ai-style':    return <AIStylingAdvisor />;
      case 'multilang':   return <MultiLanguageSupport />;
      case 'admin':
        if (!user || (user.role !== 'admin' && user.role !== 'payment_team')) {
          return (
            <div className="g-guard">
              <h2>Admin Access Only</h2>
              <p>You must be signed in as an administrator to access this page.</p>
              <button onClick={() => setPage('home')}>Go to Home</button>
            </div>
          );
        }
        return <AdminDashboard />;
      default: return <ABSDressDesigner />;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, user?.role]);

  // Loading screen while checking saved JWT
  if (authLoading) {
    return (
      <div style={{ display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', minHeight:'100vh', background:'#1A1410', gap:16 }}>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'1.8rem', fontWeight:300, color:'#C9A84C', letterSpacing:'3px' }}>
          ✦ ABS DRESS DESIGNER
        </div>
        <div style={{ width:36, height:36, border:'2px solid rgba(201,168,76,.2)', borderTop:'2px solid #C9A84C', borderRadius:'50%', animation:'g-spin .8s linear infinite' }} />
        <style>{`@keyframes g-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <AppContext.Provider value={contextValue}>
      {/* Single <style> for ALL global styles — :root vars, header, modal */}
      <style>{GLOBAL_STYLES}</style>
      {/* Fixed header — visible on every page */}
      <MemoHeader
        page={page} setPage={setPage} user={user}
        loyaltyBalance={loyaltyBalance}
        savedCartCount={savedCartCount}
        onLogout={handleLogout}
        onShowAuth={mode => setShowAuth(mode)}
      />
      {/* Page content — padding-top:70px clears fixed header */}
      <main className="gh-page">
        <React.Suspense fallback={
          <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'60vh',flexDirection:'column',gap:14}}>
            <div style={{width:32,height:32,border:'2px solid #EDE0C8',borderTop:'2px solid #C9A84C',borderRadius:'50%',animation:'g-spin .7s linear infinite'}}/>
            <div style={{fontFamily:"'Jost',sans-serif",fontSize:'.88rem',color:'#8A7060'}}>Loading...</div>
          </div>
        }>
          {renderPage}
        </React.Suspense>
      </main>
      {/* Auth modal — opened ONLY from header buttons */}
      {showAuth && (
        <AuthModal
          initialMode={showAuth}
          onClose={() => setShowAuth(null)}
          onSuccess={handleAuthSuccess}
        />
      )}
      <GlobalToast toast={toast} onDismiss={() => setToast(null)} />
    </AppContext.Provider>
  );
}